import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useAuth } from "../../context/AuthContext";
import MediTrackDashboard from "../../components/dashboard/MediTrackDashboard";
import {
  buildSummaryFromRows,
  mapProfileReportsToMediTrackRows
} from "../../components/dashboard/MediTrackDashboard/profileToMediTrack";
import { getProfile } from "../../services/user.service";
import { useTour } from "../../tour/useTour";
import { TOUR_STORAGE_KEY } from "../../tour/tourConfig";

export default function Dashboard() {
  const { user } = useAuth();
  const firstName = user?.name?.split(" ")[0] || "there";
  const { startTour, isTourActive, hasCheckedFlag } = useTour();
  const hasStartedRef = useRef(false);

  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportRows, setReportRows] = useState([]);

  const loadProfile = useCallback(async () => {
    try {
      setReportsLoading(true);
      const data = await getProfile();
      const raw = data?.reports || [];
      const rows = mapProfileReportsToMediTrackRows(raw);
      setReportRows(rows);
    } catch (e) {
      console.error("Dashboard: failed to load profile", e);
      setReportRows([]);
    } finally {
      setReportsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Auto-start tour on first visit (after data loads)
  useEffect(() => {
    // 1. Gate: Don't run if data isn't ready
    if (reportsLoading || !hasCheckedFlag) return;
    
    // 2. Guard: Don't run if it has already started in this component lifecycle
    if (hasStartedRef.current) return;

    const seen = localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
    if (!seen) {
      hasStartedRef.current = true; // Mark as started IMMEDIATELY
      
      const timer = setTimeout(() => {
        startTour({ hasReports: reportRows.length > 0 });
      }, 750);
      
      return () => clearTimeout(timer);
    }
  }, [reportsLoading, hasCheckedFlag, reportRows.length, startTour]);

  const summaryItems = useMemo(() => buildSummaryFromRows(reportRows), [reportRows]);

  const handleRestartTour = () => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    startTour({ hasReports: reportRows.length > 0 });
  };

  return (
    <div className="w-full">
      <motion.div
        className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl md:text-3xl">
            Welcome back,{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              {firstName}
            </span>
          </h1>
          <p className="mt-1 text-sm text-slate-500 md:text-base">
            Here&apos;s an overview of your health insights and reports.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="tour-restart-btn"
            onClick={handleRestartTour}
            title="Take a guided tour of MediTrack"
          >
            <AutoAwesomeIcon sx={{ fontSize: 16 }} />
            Take a Tour
          </button>
          <div className="flex items-center gap-2 rounded-full border border-emerald-200/80 bg-emerald-50/80 px-3 py-1.5 text-xs font-medium text-emerald-800 shadow-sm">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            Live sync
          </div>
        </div>
      </motion.div>

      <MediTrackDashboard
        embedded
        summaryItems={summaryItems}
        reports={reportRows}
        useDemoData={false}
        reportsLoading={reportsLoading}
        initialDelayMs={0}
      />
    </div>
  );
}
